import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';
import { Checkbox } from '../catalyst-ui-kit/checkbox';
import { useMultiSelectContext } from './MultiSelectContext';
import type { BaseOption } from './types';

interface MultiSelectOptionProps<T, B extends BaseOption<T> = BaseOption<T>> {
  option: B;
  currentIndex: number;
  isSelected: boolean;
  isActive: boolean;
  isInShiftRange: boolean;
  shiftSelectAction: 'select' | 'deselect' | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function MultiSelectOption<T>({
  option,
  currentIndex,
  isSelected,
  isActive,
  isInShiftRange,
  shiftSelectAction,
  onMouseEnter,
  onMouseLeave,
}: MultiSelectOptionProps<T>) {
  const {
    multiselect,
    optionClassName,
    iconClassName,
    getItemProps,
    handleToggleOption,
    listRef,
    optionComponent,
  } = useMultiSelectContext();

  const OptionTag = optionComponent || 'div';

  return (
    <OptionTag
      key={option.label ?? String(option.value)}
      ref={(node: HTMLElement | null) => {
        listRef.current[currentIndex] = node;
      }}
      {...getItemProps({
        role: 'option',
        tabIndex: option.disabled ? -1 : 0,
        'aria-selected': isSelected,
        'aria-disabled': option.disabled,
        onClick: (e: React.MouseEvent) => {
          if (option.disabled) return;
          if (e.shiftKey) {
            e.preventDefault();
          }
          handleToggleOption(option.value, currentIndex, e.shiftKey);
        },
        onMouseEnter,
        onMouseLeave,
      })}
      className={twMerge(
        'group flex items-center justify-between gap-3 rounded p-2 text-contentPrimary transition-colors',
        !multiselect ? 'px-4' : '',
        option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        !option.disabled ? 'hover:bg-backgroundHoverOverlay' : '',
        isActive && !option.disabled && 'bg-backgroundHoverOverlay outline-none',
        !multiselect && isSelected && 'bg-backgroundHoverOverlay',
        optionClassName
      )}
      // Allow custom option component to receive current option
      option={option}
    >
      {multiselect && (
        <Checkbox
          checked={isSelected}
          disabled={option.disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (!option.disabled) {
              handleToggleOption(option.value, currentIndex, e.shiftKey);
            }
          }}
          color="gray"
          aria-label={`Select ${option.label ?? option.value}`}
          boxClassName={twJoin(
            !option.disabled && isInShiftRange && multiselect
              ? shiftSelectAction === 'select'
                ? 'border-2 !border-contentGreen'
                : 'border-2 !border-contentRed'
              : ''
          )}
        />
      )}
      {option.icon && (
        <div
          className={cn('shrink-0 size-4 flex items-center justify-center', iconClassName)}
          aria-hidden="true"
          tabIndex={-1}
        >
          {option.icon}
        </div>
      )}
      <div className="w-full text-xs font-normal">
        {option.display ?? option.label ?? String(option.value)}
      </div>
    </OptionTag>
  );
}
